import { removeSessionData } from '../../../../api/utils/apollo/session/sessionToken.utils'
import { getRefreshToken, removeRefreshToken } from '../../../../api/utils/auth/authStorage'
import {
  fetchRetailerPlatformMeshClient,
  fetchRetailersClient,
} from '../../../../legacy/apollo/dashboardClient'
import { storeCurrentRedirectUrl } from '../../../../routes/logged-in/utils/loggedIn.utils'
import clientEnv from '../../../../utils/global/clientEnv'
import { errors } from '../../../../utils/error-handling/errors'
import { handleSsoReauth } from './handleSsoReauth'
import { trackLogout } from './trackLogout'

export enum LogoutReason {
  UserInitiated,
  TokenExpired,
  DisabledAccount,
}

/**
 * Logs the user out of the application.
 * This will:
 * - Empty all apollo stores for all clients
 * - Delete the session token
 * - Redirect to the login screen. If `tokenExpired` is true, it will also show a message on
 *   the login screen stating that fact.
 *
 * @param logoutReason
 * @param isInternal - Optional flag to indicate if the user is an internal Instacart user
 * @returns
 */
export const logout = async (
  logoutReason: LogoutReason = LogoutReason.UserInitiated,
  isInternal?: boolean
) => {
  // Check if we should redirect to SSO re-auth instead of logout
  if (logoutReason === LogoutReason.TokenExpired) {
    const didRedirectToSsoReauth = await handleSsoReauth()
    if (didRedirectToSsoReauth) {
      return
    }
  }

  const storeResetPromises = [fetchRetailersClient(), fetchRetailerPlatformMeshClient()].map(
    client => client.clearStore()
  )

  trackLogout({
    isExpired: logoutReason === LogoutReason.TokenExpired,
  })

  // Get refresh token BEFORE clearing it so we can send it to the logout endpoint
  const refreshToken = await getRefreshToken()

  await Promise.all([...storeResetPromises, removeSessionData(), removeRefreshToken()])

  // Build the return URI based on logout reason
  let returnUri = `${window.location.origin}/public/log-in`

  if (logoutReason != null) {
    const queryParam =
      logoutReason === LogoutReason.TokenExpired
        ? 'sessionExpired'
        : logoutReason === LogoutReason.DisabledAccount
          ? 'disabled'
          : undefined

    if (queryParam != null) {
      // If the user is getting logged out because of a token expiration
      // we try storing the current URL so we can redirect them later
      storeCurrentRedirectUrl()

      // Build query parameters
      const queryParams = new URLSearchParams()
      queryParams.set(queryParam, '1')

      // Add isInternal parameter only for internal users when being logged out for disabled account
      if (logoutReason === LogoutReason.DisabledAccount && isInternal === true) {
        queryParams.set('isInternal', '1')
      }

      returnUri = `${window.location.origin}/public/log-in?${queryParams.toString()}`
    }
  }

  // Two-step logout:
  // 1. POST to revoke OAuth tokens (refresh token sent as param)
  // 2. GET redirect to clear session cookie (requires same-origin for Set-Cookie to work)

  // Step 1: POST to revoke tokens
  try {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const revokeParams: Record<string, string> = {}
    if (refreshToken) {
      revokeParams.refresh_token = refreshToken
    }
    await fetch(`${clientEnv.PUBLIC_CLIENT_AUTH_SERVICE_BASE_URL}/logout/revoke`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      credentials: 'include', // Include cookies for session identification
      body: new URLSearchParams(revokeParams),
    })
  } catch (error) {
    // Log errors for monitoring, but don't block logout
    errors.captureException(
      error instanceof Error ? error : new Error('Unknown logout revoke error'),
      'Error calling logout revoke endpoint',
      {
        extra: {
          logoutReason,
          errorDetails: error,
        },
      }
    )
  }

  // Step 2: GET redirect to clear session cookie
  // Browser navigates to auth_service domain, clearing auth session cookie
  const logoutUrl = new URL(`${clientEnv.PUBLIC_CLIENT_AUTH_SERVICE_BASE_URL}/logout`)
  logoutUrl.searchParams.set('return_uri', returnUri)
  return window.location.replace(logoutUrl.toString())
}
